/**
 * Turning "besok, jam 10 pagi" into a delivery date without inventing a clock time.
 *
 * The extractor resolves the date to ISO but keeps the time exactly as the customer said
 * it, because half the time it is not a clock reading: "pagi", "sebelum jam 10", "sore
 * aja". Mapping those onto a timestamp would put a precise commitment in front of a
 * courier that nobody actually made — 08:00 for "pagi" is a promise the shop never gave.
 *
 * So a stated hour is used, and anything else is handed back as text for the order note
 * where a human reads it. Both, never a guess in between.
 */

/**
 * Indonesian conversations state the hour on a 12-hour clock and lean on the period word
 * to disambiguate — "jam 5 sore" is 17:00 and is written far more often than "17:00".
 * Dropping the period word is not an option: it would book a five-in-the-morning delivery.
 */
const PERIODS = ["pagi", "siang", "sore", "malam"] as const;

type Period = (typeof PERIODS)[number];

/**
 * An hour, optionally introduced by "jam"/"pukul" and optionally followed by a period.
 *
 * Not anchored to the end of the string on purpose: an earlier version was, and "jam 10
 * pagi" — the commonest phrasing there is — failed to match because of the trailing word,
 * silently booking the delivery at midnight.
 */
const CLOCK = new RegExp(
  `(?:^|\\s|jam\\s*|pukul\\s*)(\\d{1,2})(?:[.:](\\d{2}))?\\s*(${PERIODS.join("|")})?\\b`,
  "i"
);

/** The whole string is a time and nothing else — so the note has nothing left to add. */
const ONLY_A_TIME = new RegExp(
  `^\\s*(?:jam|pukul)?\\s*\\d{1,2}(?:[.:]\\d{2})?\\s*(?:${PERIODS.join("|")})?\\s*$`,
  "i"
);

/**
 * A 12-hour reading plus a period word, as a 24-hour hour.
 *
 * Midnight and midday are the two that do not follow the +12 rule: "jam 12 malam" is 00:00
 * and "jam 12 siang" is 12:00, so both are handled before the general case.
 */
const toTwentyFourHour = (hour: number, period: Period | null): number => {
  if (period === null) return hour;

  if (hour === 12) return period === "malam" || period === "pagi" ? 0 : 12;

  if (period === "pagi") return hour;

  /** siang, sore and malam all mean the afternoon or later. */
  return hour < 12 ? hour + 12 : hour;
};

export type ParsedDelivery = {
  /** Undefined when the conversation never settled a date. */
  date: Date | undefined;
  /** The time phrase, when it says more than the timestamp does. Belongs in the order note. */
  unparsedTime: string | null;
};

export const parseDeliveryDateTime = (
  deliveryDate: string | null,
  deliveryTime: string | null
): ParsedDelivery => {
  if (!deliveryDate) {
    return { date: undefined, unparsedTime: deliveryTime };
  }

  /**
   * Split rather than `new Date(string)`: an ISO date with no zone is read as UTC, which
   * lands on the previous day for anyone east of Greenwich — every user of this shop.
   */
  const [year, month, day] = deliveryDate.slice(0, 10).split("-").map(Number);

  if (!year || !month || !day) {
    return { date: undefined, unparsedTime: deliveryTime };
  }

  const match = deliveryTime?.match(CLOCK);

  const statedHour = match ? parseInt(match[1], 10) : NaN;
  const minutes = match?.[2] ? parseInt(match[2], 10) : 0;
  const period = (match?.[3]?.toLowerCase() as Period | undefined) ?? null;

  const isClockTime =
    Number.isFinite(statedHour) &&
    statedHour >= 0 &&
    statedHour <= 23 &&
    minutes <= 59 &&
    /**
     * "jam 15 sore" contradicts itself. Rather than pick a winner, the whole phrase is
     * handed to the order note — a florist reading "jam 15 sore" understands it, and a
     * timestamp guessed from it could be three hours wrong either way.
     */
    !(period !== null && statedHour > 12);

  const hours = isClockTime ? toTwentyFourHour(statedHour, period) : 0;

  return {
    /** Midnight when no hour was stated — never a plausible-looking guess like 09:00. */
    date: new Date(year, month - 1, day, hours, isClockTime ? minutes : 0),
    /**
     * Kept whenever the words say something the timestamp cannot. "jam 10 pagi" is fully
     * captured by 10:00 and is dropped; "sebelum jam 10" is a deadline rather than a slot,
     * so the hour is used AND the qualifier survives for the florist to read.
     */
    unparsedTime:
      deliveryTime && (!isClockTime || !ONLY_A_TIME.test(deliveryTime))
        ? deliveryTime
        : null,
  };
};
